import { type FC, type MouseEvent } from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { type FolderListType } from "./MediaSelector";
import { type MediaPathType } from "./MediaUtilities";

interface Props {
    id: string;
    name: string;
    open: boolean;
    current: Array<MediaPathType>;
    subFolders: FolderListType | boolean | null;
    onToggle: (path: Array<MediaPathType>) => void;
    onChange: (path: Array<MediaPathType>) => void;
    layer: number;
}

export const Folder: FC<Props> = ({
    id,
    name,
    open,
    current,
    subFolders,
    onToggle,
    onChange,
    layer,
}) => {
    const onToggleClick = (e: MouseEvent): void => {
        onToggle([{
            id,
            name,
        }]);

        e.stopPropagation();
    };

    const onSelectClick = (e: MouseEvent): void => {
        onChange([{
            id,
            name,
        }]);

        e.stopPropagation();
    };

    const onChildToggle = (value: Array<MediaPathType>): void => {
        onToggle([
            {
                id,
                name,
            },
            ...value,
        ]);
    };

    const onChildSelect = (value: Array<MediaPathType>): void => {
        onChange([
            {
                id,
                name,
            },
            ...value,
        ]);
    };

    const className = [ "folder" ];

    const headingClass = [ "folder-header" ];

    if(current?.[layer]?.id === id) {
        if(current.length - 1 === layer) {
            headingClass.push("current");
        }
    } else {
        current = [];
    }

    let subFoldersContent = null;
    let openIcon = false;
    let children = false;

    if(subFolders !== null) {
        className.push("contains");
        openIcon = true;
        children = true;

        if(open) {
            if(subFolders === true) {
                subFoldersContent = (
                    <div className="sub-folders">
                        <div className="folder-header loading">
                            <span className="folder-heading">
                                <div className="folder-icon">
                                    <FontAwesomeLight icon="spinner" className="fa-pulse" />
                                </div>
                                <div className="folder-name">
                                    Loading...
                                </div>
                            </span>
                        </div>
                    </div>
                );
            } else if(subFolders !== false) {
                subFoldersContent = (
                    <div className={"sub-folders"}>
                        {Object.keys(subFolders).map(id => {
                            if(typeof subFolders !== "object" || subFolders === null) {
                                return null;
                            }

                            const sub = subFolders[id];

                            if(!sub) {
                                return null;
                            }

                            return (
                                <Folder
                                    id={id}
                                    key={sub.name}
                                    current={current}
                                    open={sub.open}
                                    name={sub.name}
                                    subFolders={sub.children}
                                    onToggle={onChildToggle}
                                    onChange={onChildSelect}
                                    layer={layer + 1}
                                />
                            );
                        })}
                    </div>
                );
            }
        }
    }

    return (
        <div className={className.join(" ")}>
            <div className={headingClass.join(" ")} onClick={onSelectClick}>
                <div
                    className={
                        "folder-close-toggle" +
                        (children ? " children" : "") +
                        (openIcon ? " hidden" : "")
                    }
                    onClick={onToggleClick}
                >
                    <FontAwesomeLight
                        icon={"angle-" + (open ? "down" : "right") as IconProp}
                    />
                </div>
                <span className="folder-heading">
                    <div className="folder-icon">
                        <FontAwesomeLight icon={(open ? "folder-open" : "folder")} />
                    </div>
                    <div className="folder-name">
                        {name}
                    </div>
                </span>
            </div>
            {subFoldersContent}
        </div>
    );
};
